import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { leadsApi, usersApi } from '../../services/api';
import { useAuth } from '../../store/authContext';
import { PlusIcon, SearchIcon, TrashIcon, UploadIcon } from '../../utils/icons';
import { formatDate } from '../../utils/formatters';
import Modal from '../../components/Modal';
import ExcelUploadModal from './ExcelUploadModal';
import './LeadList.css';

const STAGES = ['All', 'New', 'Contacted', 'Site Visit', 'Negotiation', 'Closed', 'Lost'];
const SOURCES = ['All', '99acres', 'MagicBricks', 'Housing.com', 'Manual', 'Excel Import'];
const PAGE_SIZE = 50;

export default function LeadList() {
  const { user: currentUser } = useAuth();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('All');
  const [sourceFilter, setSourceFilter] = useState('All');
  const [unassignedFilter, setUnassignedFilter] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const [selected, setSelected] = useState(new Set());

  const [showCreate, setShowCreate] = useState(false);
  const [showExcelUpload, setShowExcelUpload] = useState(false);
  const [deletingLead, setDeletingLead] = useState(null);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [showBulkStage, setShowBulkStage] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);

  const fetchLeads = useCallback(async () => {
    try {
      setError('');
      const params = { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE };
      if (stageFilter !== 'All') params.stage = stageFilter;
      if (sourceFilter !== 'All') params.source = sourceFilter;
      if (search.trim()) params.search = search.trim();
      if (unassignedFilter) params.unassigned = true;
      const res = await leadsApi.list(params);
      setLeads(res.data.items);
      setTotal(res.data.total);
    } catch {
      setError('Failed to load leads.');
    } finally {
      setLoading(false);
    }
  }, [stageFilter, sourceFilter, search, unassignedFilter, page]);

  const fetchUsers = useCallback(async () => {
    if (currentUser?.role !== 'admin') return;
    try {
      const res = await usersApi.list();
      setUsers(res.data);
    } catch {
      // Non-critical — user names may not resolve
    }
  }, [currentUser?.role]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Build a user ID → name map for display
  const userMap = useMemo(() => {
    const map = {};
    for (const u of users) map[u.id] = u.name;
    return map;
  }, [users]);

  const resolveUserName = (userId) => {
    if (!userId) return null;
    if (userId === currentUser?.id) return 'You';
    return userMap[userId] || null;
  };

  // Client-side sort (server already handles filtering)
  const sorted = useMemo(() => {
    const data = [...leads];
    data.sort((a, b) => {
      let av = a[sort.key], bv = b[sort.key];
      if (sort.key === 'created_at') { av = new Date(av); bv = new Date(bv); }
      if (av < bv) return sort.dir === 'asc' ? -1 : 1;
      if (av > bv) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [leads, sort]);

  const toggleSort = (key) => {
    setSort((s) => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const toggleAll = () => {
    if (selected.size === sorted.length) setSelected(new Set());
    else setSelected(new Set(sorted.map(l => l.id)));
  };

  const toggleOne = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const canDelete = (lead) => {
    if (currentUser?.role === 'admin') return true;
    return lead.created_by === currentUser?.id;
  };

  const handleCreated = () => { setShowCreate(false); fetchLeads(); };
  const handleDeleted = () => { setDeletingLead(null); fetchLeads(); };
  const handleBulkDone = () => {
    setShowBulkAssign(false);
    setShowBulkStage(false);
    setShowBulkDelete(false);
    setSelected(new Set());
    fetchLeads();
  };

  if (loading) {
    return (
      <div className="lead-list-page">
        <div className="ll-loading">
          <div className="ll-loading-spinner" />
          <p>Loading leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lead-list-page">
      {/* Header */}
      <div className="page-header animate-fade-up">
        <div className="page-header-top">
          <div>
            <h1 className="page-title">Leads</h1>
            <p className="page-subtitle">{total} lead{total !== 1 ? 's' : ''}</p>
          </div>
          <div className="page-actions">
            <button className="btn btn-ghost btn-sm">
              <DownloadIcon /> Export
            </button>
            {currentUser?.role === 'admin' && (
              <button className="btn btn-ghost btn-sm" onClick={() => setShowExcelUpload(true)}>
                <UploadIcon /> Import Excel
              </button>
            )}
            <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
              <PlusIcon /> Add lead
            </button>
          </div>
        </div>
      </div>

      {error && <div className="ll-error animate-fade-up">{error}</div>}

      {/* Filters */}
      <div className="lead-filters animate-fade-up" style={{ animationDelay: '60ms' }}>
        <div className="lead-search-wrap">
          <span className="lead-search-icon"><SearchIcon /></span>
          <input
            className="input lead-search"
            placeholder="Search by name, phone, email..."
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
          />
          {search && (
            <button className="lead-search-clear" onClick={() => { setPage(1); setSearch(''); }}>
              <XIcon />
            </button>
          )}
        </div>

        <div className="lead-filter-chips">
          <div className="filter-chip-group">
            {STAGES.map((s) => (
              <button
                key={s}
                className={`filter-chip ${stageFilter === s ? 'filter-chip--active' : ''}`}
                onClick={() => { setPage(1); setStageFilter(s); }}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="filter-separator" />
          <div className="filter-chip-group">
            {SOURCES.map((s) => (
              <button
                key={s}
                className={`filter-chip ${sourceFilter === s ? 'filter-chip--active' : ''}`}
                onClick={() => { setPage(1); setSourceFilter(s); }}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="filter-separator" />
          <div className="filter-chip-group">
            <button
              className={`filter-chip ${unassignedFilter ? 'filter-chip--active' : ''}`}
              onClick={() => { setPage(1); setUnassignedFilter((v) => !v); }}
            >
              Unassigned
            </button>
          </div>
        </div>
      </div>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="bulk-bar animate-fade-in">
          <span className="bulk-count">{selected.size} selected</span>
          {currentUser?.role === 'admin' && (
            <button className="btn btn-ghost btn-sm" onClick={() => setShowBulkAssign(true)}>
              <AssignIcon /> Assign
            </button>
          )}
          {currentUser?.role === 'admin' && (
            <button className="btn btn-ghost btn-sm" onClick={() => setShowBulkStage(true)}>
              <TagIcon /> Change stage
            </button>
          )}
          <button
            className="btn btn-sm"
            style={{ color: 'var(--danger)', borderColor: 'var(--danger-dim)', background: 'var(--danger-dim)' }}
            onClick={() => setShowBulkDelete(true)}
          >
            <TrashIcon /> Delete
          </button>
          <button className="btn-icon" onClick={() => setSelected(new Set())}><XIcon /></button>
        </div>
      )}

      {/* Table */}
      <div className="card lead-table-card animate-fade-up" style={{ animationDelay: '100ms', padding: 0 }}>
        <div className="lead-table-wrap">
          <table className="lead-table">
            <thead>
              <tr>
                <th className="th-check">
                  <input
                    type="checkbox"
                    className="lead-checkbox"
                    checked={selected.size === sorted.length && sorted.length > 0}
                    onChange={toggleAll}
                  />
                </th>
                <SortTh label="Name" sortKey="name" sort={sort} onSort={toggleSort} />
                <th>Source</th>
                <th>Address</th>
                <SortTh label="Stage" sortKey="stage" sort={sort} onSort={toggleSort} />
                <th>Budget</th>
                <th>Assigned To</th>
                <SortTh label="Added" sortKey="created_at" sort={sort} onSort={toggleSort} />
                <th className="th-actions" />
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={9} className="lead-empty">
                    <div className="lead-empty-content">
                      <EmptyIcon />
                      <p>No leads match your filters</p>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => { setPage(1); setSearch(''); setStageFilter('All'); setSourceFilter('All'); setUnassignedFilter(false); }}
                      >
                        Clear filters
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                sorted.map((lead) => (
                  <tr
                    key={lead.id}
                    className={selected.has(lead.id) ? 'lead-row--selected' : ''}
                  >
                    <td className="td-check">
                      <input
                        type="checkbox"
                        className="lead-checkbox"
                        checked={selected.has(lead.id)}
                        onChange={() => toggleOne(lead.id)}
                      />
                    </td>
                    <td>
                      <Link to={`/leads/${lead.id}`} className="lead-name-link">
                        <div className="lead-avatar-sm">
                          {lead.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                        </div>
                        <div>
                          <div className="lead-name">{lead.name}</div>
                          <div className="lead-phone">{lead.phone}</div>
                        </div>
                      </Link>
                    </td>
                    <td><span className="source-badge">{lead.source}</span></td>
                    <td className="lead-address">{lead.address || '—'}</td>
                    <td><StageBadge stage={lead.stage} /></td>
                    <td><span className="lead-budget">{lead.budget || '—'}</span></td>
                    <td>
                      {lead.assigned_to ? (
                        <span className="lead-agent-pill">{resolveUserName(lead.assigned_to) || 'Assigned'}</span>
                      ) : (
                        <span className="lead-unassigned-pill">Unassigned</span>
                      )}
                    </td>
                    <td className="lead-time">{formatDate(lead.created_at)}</td>
                    <td className="td-actions">
                      <div className="row-actions">
                        <Link to={`/leads/${lead.id}`} className="btn-icon" title="View">
                          <EyeIcon />
                        </Link>
                        {canDelete(lead) && (
                          <button
                            className="btn-icon"
                            title="Delete"
                            onClick={(e) => { e.stopPropagation(); setDeletingLead(lead); }}
                          >
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="table-footer">
          <p className="table-count">{total} lead{total !== 1 ? 's' : ''}</p>
          {total > PAGE_SIZE && (
            <div className="ll-pagination">
              <button
                className="btn btn-ghost btn-sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ← Prev
              </button>
              <span className="ll-page-info">Page {page} of {Math.ceil(total / PAGE_SIZE)}</span>
              <button
                className="btn btn-ghost btn-sm"
                disabled={page >= Math.ceil(total / PAGE_SIZE)}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create lead modal */}
      {showCreate && (
        <LeadFormModal
          users={users}
          currentUser={currentUser}
          onClose={() => setShowCreate(false)}
          onSaved={handleCreated}
        />
      )}

      {/* Delete confirmation */}
      {deletingLead && (
        <DeleteLeadModal
          lead={deletingLead}
          onClose={() => setDeletingLead(null)}
          onDeleted={handleDeleted}
        />
      )}

      {/* Excel upload modal */}
      {showExcelUpload && (
        <ExcelUploadModal
          onClose={() => setShowExcelUpload(false)}
          onImported={() => { setShowExcelUpload(false); fetchLeads(); }}
        />
      )}

      {/* Bulk action modals */}
      {showBulkAssign && (
        <BulkAssignModal
          selectedIds={[...selected]}
          users={users}
          onClose={() => setShowBulkAssign(false)}
          onDone={handleBulkDone}
        />
      )}
      {showBulkStage && (
        <BulkStageModal
          selectedIds={[...selected]}
          onClose={() => setShowBulkStage(false)}
          onDone={handleBulkDone}
        />
      )}
      {showBulkDelete && (
        <BulkDeleteModal
          selectedIds={[...selected]}
          onClose={() => setShowBulkDelete(false)}
          onDone={handleBulkDone}
        />
      )}
    </div>
  );
}


/* ─── Lead Form Modal (Create) ──────────────────────────────────────────── */

function LeadFormModal({ users, currentUser, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', phone: '', email: '', budget: '',
    address: '', remark: '', reference_number: '', type: '',
    project_name: '', notes: '',
    assigned_to: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const data = { ...form, source: 'Manual' };
      if (!data.assigned_to) delete data.assigned_to;
      if (!data.email) delete data.email;
      await leadsApi.create(data);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create lead.');
    } finally {
      setSaving(false);
    }
  };

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  return (
    <Modal title="Add Lead" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          {error && <div className="modal-error">{error}</div>}

          <div className="ll-form-row">
            <div className="form-group">
              <label className="section-label">Name *</label>
              <input className="input" value={form.name} onChange={set('name')} placeholder="Lead name" required />
            </div>
            <div className="form-group">
              <label className="section-label">Phone *</label>
              <input className="input" value={form.phone} onChange={set('phone')} placeholder="+91 XXXXX XXXXX" required />
            </div>
          </div>

          <div className="ll-form-row">
            <div className="form-group">
              <label className="section-label">Email</label>
              <input className="input" type="email" value={form.email} onChange={set('email')} placeholder="email@example.com" />
            </div>
            <div className="form-group">
              <label className="section-label">Budget</label>
              <input className="input" value={form.budget} onChange={set('budget')} placeholder="e.g. ₹85L" />
            </div>
          </div>

          <div className="ll-form-row">
            <div className="form-group">
              <label className="section-label">Address</label>
              <input className="input" value={form.address} onChange={set('address')} placeholder="Full address" />
            </div>
            <div className="form-group">
              <label className="section-label">Project Name</label>
              <input className="input" value={form.project_name} onChange={set('project_name')} placeholder="e.g. Prestige Lakeside" />
            </div>
          </div>

          <div className="ll-form-row">
            <div className="form-group">
              <label className="section-label">Type</label>
              <input className="input" value={form.type} onChange={set('type')} placeholder="e.g. 3 BHK Apartment" />
            </div>
            <div className="form-group">
              <label className="section-label">Reference Number</label>
              <input className="input" value={form.reference_number} onChange={set('reference_number')} placeholder="Reference #" />
            </div>
          </div>

          <div className="form-group">
            <label className="section-label">Remark</label>
            <textarea className="input ll-textarea" value={form.remark} onChange={set('remark')} placeholder="Any remarks..." rows={2} />
          </div>

          {currentUser?.role === 'admin' && users.length > 0 && (
            <div className="form-group">
              <label className="section-label">Assign To</label>
              <select className="input ll-select" value={form.assigned_to} onChange={set('assigned_to')}>
                <option value="">Unassigned</option>
                {users.filter(u => u.is_active).map((u) => (
                  <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label className="section-label">Notes</label>
            <textarea className="input ll-textarea" value={form.notes} onChange={set('notes')} placeholder="Any additional notes..." rows={3} />
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Creating...' : 'Create Lead'}
          </button>
        </div>
      </form>
    </Modal>
  );
}


/* ─── Delete Confirmation Modal ─────────────────────────────────────────── */

function DeleteLeadModal({ lead, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setDeleting(true);
    setError('');
    try {
      await leadsApi.delete(lead.id);
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete lead.');
      setDeleting(false);
    }
  };

  return (
    <Modal title="Delete Lead" size="sm" onClose={onClose}>
      <div className="modal-body">
        {error && <div className="modal-error">{error}</div>}
        <p className="delete-msg">
          Are you sure you want to delete <strong>{lead.name}</strong>?
        </p>
        <p className="delete-hint">
          This lead will be archived and no longer visible in the leads list.
        </p>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-danger" onClick={handleConfirm} disabled={deleting}>
          {deleting ? 'Deleting...' : 'Delete Lead'}
        </button>
      </div>
    </Modal>
  );
}


/* ─── Sub-components ─────────────────────────────────────────────────────── */

function SortTh({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  return (
    <th className={`th-sort ${active ? 'th-sort--active' : ''}`} onClick={() => onSort(sortKey)}>
      {label}
      <span className="sort-icon">{active ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}</span>
    </th>
  );
}

function StageBadge({ stage }) {
  const map = {
    'New':         'new',
    'Contacted':   'contact',
    'Site Visit':  'visit',
    'Negotiation': 'negotiation',
    'Closed':      'closed',
    'Lost':        'lost',
  };
  const cls = map[stage] || 'default';
  return (
    <span className={`badge badge-${cls}`}>
      <span className="badge-dot" />
      {stage}
    </span>
  );
}


/* ─── Bulk Action Modals ────────────────────────────────────────────────── */

function BulkAssignModal({ selectedIds, users, onClose, onDone }) {
  const [agentId, setAgentId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleConfirm = async () => {
    if (!agentId) return;
    setSaving(true);
    setError('');
    try {
      const res = await leadsApi.bulkAssign(selectedIds, agentId);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to assign leads.');
      setSaving(false);
    }
  };

  if (result) {
    return (
      <Modal title="Bulk Assign" size="sm" onClose={onDone}>
        <div className="modal-body">
          <p>{result.affected} lead{result.affected !== 1 ? 's' : ''} assigned successfully.</p>
          {result.skipped > 0 && <p>{result.skipped} lead{result.skipped !== 1 ? 's' : ''} skipped (not found or inactive).</p>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onDone}>Done</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Bulk Assign" size="sm" onClose={onClose}>
      <div className="modal-body">
        {error && <div className="modal-error">{error}</div>}
        <p>Assign <strong>{selectedIds.length}</strong> lead{selectedIds.length !== 1 ? 's' : ''} to:</p>
        <select className="input ll-select" value={agentId} onChange={(e) => setAgentId(e.target.value)}>
          <option value="">Select agent...</option>
          {users.filter(u => u.is_active).map((u) => (
            <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
          ))}
        </select>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={handleConfirm} disabled={saving || !agentId}>
          {saving ? 'Assigning...' : 'Assign'}
        </button>
      </div>
    </Modal>
  );
}


function BulkStageModal({ selectedIds, onClose, onDone }) {
  const [stage, setStage] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const stageOptions = STAGES.filter(s => s !== 'All');

  const handleConfirm = async () => {
    if (!stage) return;
    setSaving(true);
    setError('');
    try {
      const res = await leadsApi.bulkChangeStage(selectedIds, stage);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change stage.');
      setSaving(false);
    }
  };

  if (result) {
    return (
      <Modal title="Bulk Change Stage" size="sm" onClose={onDone}>
        <div className="modal-body">
          <p>{result.affected} lead{result.affected !== 1 ? 's' : ''} updated.</p>
          {result.skipped > 0 && <p>{result.skipped} lead{result.skipped !== 1 ? 's' : ''} skipped (already at this stage or not found).</p>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onDone}>Done</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Bulk Change Stage" size="sm" onClose={onClose}>
      <div className="modal-body">
        {error && <div className="modal-error">{error}</div>}
        <p>Change stage for <strong>{selectedIds.length}</strong> lead{selectedIds.length !== 1 ? 's' : ''} to:</p>
        <select className="input ll-select" value={stage} onChange={(e) => setStage(e.target.value)}>
          <option value="">Select stage...</option>
          {stageOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={handleConfirm} disabled={saving || !stage}>
          {saving ? 'Updating...' : 'Change Stage'}
        </button>
      </div>
    </Modal>
  );
}


function BulkDeleteModal({ selectedIds, onClose, onDone }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleConfirm = async () => {
    setDeleting(true);
    setError('');
    try {
      const res = await leadsApi.bulkDelete(selectedIds);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete leads.');
      setDeleting(false);
    }
  };

  if (result) {
    return (
      <Modal title="Bulk Delete" size="sm" onClose={onDone}>
        <div className="modal-body">
          <p>{result.affected} lead{result.affected !== 1 ? 's' : ''} deleted.</p>
          {result.skipped > 0 && <p>{result.skipped} lead{result.skipped !== 1 ? 's' : ''} skipped (insufficient permissions).</p>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onDone}>Done</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Bulk Delete" size="sm" onClose={onClose}>
      <div className="modal-body">
        {error && <div className="modal-error">{error}</div>}
        <p className="delete-msg">
          Are you sure you want to delete <strong>{selectedIds.length}</strong> lead{selectedIds.length !== 1 ? 's' : ''}?
        </p>
        <p className="delete-hint">
          These leads will be archived and no longer visible in the leads list.
        </p>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-danger" onClick={handleConfirm} disabled={deleting}>
          {deleting ? 'Deleting...' : `Delete ${selectedIds.length} Lead${selectedIds.length !== 1 ? 's' : ''}`}
        </button>
      </div>
    </Modal>
  );
}


/* ─── Page-specific icons ───────────────────────────────────────────────── */

function XIcon() {
  return <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>;
}
function DownloadIcon() {
  return <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M8 2v8M5 7l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 13h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
}
function EyeIcon() {
  return <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.4"/><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4"/></svg>;
}
function AssignIcon() {
  return <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.4"/><path d="M1 14c0-2.761 2.239-5 5-5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M11 9v5M8.5 11.5h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
}
function TagIcon() {
  return <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M2 2h5.5l6 6-5.5 5.5-6-6V2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/><circle cx="5" cy="5" r="1" fill="currentColor"/></svg>;
}
function EmptyIcon() {
  return <svg width="36" height="36" viewBox="0 0 40 40" fill="none"><rect x="4" y="8" width="32" height="26" rx="3" stroke="currentColor" strokeWidth="1.5" opacity="0.3"/><path d="M12 18h16M12 24h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.3"/><circle cx="30" cy="30" r="7" fill="var(--bg-base)" stroke="currentColor" strokeWidth="1.5" opacity="0.5"/><path d="M27 30h6M30 27v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/></svg>;
}
